import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Phone, ArrowDown, ShieldCheck, Clock, MapPin } from 'lucide-react';
import { HERO } from '@/constants/testIds';
import { KineticLines } from './KineticLines';

const HERO_IMG =
	'https://images.pexels.com/photos/8931963/pexels-photo-8931963.jpeg';

export const Hero = () => {
	const ref = useRef(null);
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ['start start', 'end start'],
	});
	const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
	const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
	const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-15%']);

	return (
		<section
			ref={ref}
			data-testid={HERO.root}
			id="top"
			className="relative w-full min-h-screen overflow-hidden bg-brand-navy"
		>
			{/* Parallax background */}
			<motion.div
				aria-hidden
				style={{ y: bgY, scale: bgScale }}
				className="absolute inset-0"
			>
				<img
					src={HERO_IMG}
					alt=""
					className="w-full h-full object-cover"
					loading="eager"
					fetchPriority="high"
				/>
			</motion.div>

			{/* Overlays */}
			<div
				aria-hidden
				className="absolute inset-0 bg-[#0D1B2A]/70"
			/>
			<div
				aria-hidden
				className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A] via-transparent to-[#0D1B2A]/30"
			/>
			<div aria-hidden className="absolute inset-0 grain" />

			{/* Vertical rule chips */}
			<div className="absolute top-24 sm:top-28 left-4 sm:left-8 right-4 sm:right-8 flex items-center justify-between text-[10px] sm:text-[11px] font-chivo font-bold uppercase tracking-[0.3em] text-white/60 z-10">
				<motion.span
					initial={{ opacity: 0, y: -8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.15, duration: 0.6 }}
					className="flex items-center gap-2"
				>
					<span className="w-1.5 h-1.5 bg-brand-red pulse-dot" />
					Dispatch Live · Nationwide
				</motion.span>
				<motion.span
					initial={{ opacity: 0, y: -8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.25, duration: 0.6 }}
					className="hidden sm:inline"
				>
					USA · 24/7/365
				</motion.span>
			</div>

			{/* Content */}
			<motion.div
				style={{ y: textY }}
				className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-8 pt-32 sm:pt-40 pb-24 sm:pb-32 min-h-screen flex flex-col justify-center"
			>
				<div className="max-w-[1200px]">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.05, duration: 0.5 }}
						className="mb-6 sm:mb-10 inline-flex items-center gap-3 border border-white/25 px-3 py-1.5 text-[11px] sm:text-[12px] font-chivo font-bold uppercase tracking-[0.28em] text-white/80"
					>
						<span className="w-2 h-2 bg-brand-red pulse-dot" />
						Emergency Roadside · Est. 24/7
					</motion.div>

					<KineticLines
						data-testid={HERO.headline}
						className="font-anton text-white text-[44px] leading-[0.95] sm:text-[80px] md:text-[110px] lg:text-[148px] tracking-tight"
						lines={[
							'24/7 Roadside',
							'Assistance —',
							<span key="l3">
								Help Arrives{' '}
								<span className="text-brand-red">Fast</span>,
							</span>,
							'Anywhere in the US.',
						]}
					/>

					<motion.p
						data-testid={HERO.subline}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.9, duration: 0.7 }}
						className="mt-8 sm:mt-10 max-w-2xl font-plex text-[15px] sm:text-lg text-white/80 leading-relaxed"
					>
						Towing, jump starts, flat tires, lockouts, fuel delivery
						and winch-outs. Licensed, insured and dispatched
						nationwide from Sheridan, Wyoming — one phone call
						away.
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 1.05, duration: 0.6 }}
						className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
					>
						<a
							href="tel:+13072224082"
							data-testid={HERO.callCta}
							className="btn-red w-full sm:w-auto !py-5 sm:!py-6 !px-6 sm:!px-8 text-lg sm:text-2xl pulse-ring"
						>
							<Phone className="w-6 h-6" strokeWidth={2.5} />
							Call Now: (307) 222-4082
						</a>
						<a
							href="#services"
							className="btn-outline-navy !border-white !text-white hover:!bg-white hover:!text-brand-navy w-full sm:w-auto justify-center"
						>
							See Services
							<ArrowDown className="w-4 h-4" />
						</a>
					</motion.div>

					{/* Bottom mini bar */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 1.4, duration: 0.6 }}
						className="mt-12 sm:mt-16 grid grid-cols-3 max-w-2xl border-t border-white/20"
					>
						{[
							{ icon: ShieldCheck, label: 'Licensed & Insured' },
							{ icon: Clock, label: 'Under 60s to Dispatch' },
							{ icon: MapPin, label: 'All 50 States' },
						].map((item, i) => (
							<div
								key={i}
								className="py-4 flex items-center gap-2 sm:gap-3 border-l border-white/20 first:border-l-0 px-2 sm:px-4"
							>
								<item.icon
									className="w-4 h-4 sm:w-5 sm:h-5 text-brand-red flex-shrink-0"
									strokeWidth={2}
								/>
								<span className="font-chivo font-bold text-[10px] sm:text-xs uppercase tracking-widest text-white/90 leading-tight">
									{item.label}
								</span>
							</div>
						))}
					</motion.div>
				</div>
			</motion.div>

			{/* Scroll hint */}
			<motion.div
				data-testid={HERO.scrollHint}
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 1.6, duration: 0.8 }}
				className="hidden md:flex absolute bottom-8 right-8 z-10 flex-col items-center gap-2 text-white/60"
			>
				<span className="font-chivo font-bold text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-rl]">
					Scroll
				</span>
				<motion.span
					animate={{ y: [0, 8, 0] }}
					transition={{
						duration: 1.6,
						repeat: Infinity,
						ease: 'easeInOut',
					}}
					className="block w-px h-10 bg-white/60"
				/>
			</motion.div>
		</section>
	);
};

export default Hero;
